import React, { useState, useEffect, useCallback } from 'react';
import socketService from '../services/socketService';
import Layout from '../components/Layout';
import ResilientImage from '../components/ResilientImage';
import moment from 'moment-timezone';

const PendingList = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ totalPages: 1, currentPage: 1, totalRows: 0 });

    const [filters, setFilters] = useState({
        username: '', 
        number: '', 
        security_code: '', 
        type: '', 
        status: '', 
        start_date: '', 
        end_date: '', 
        page: 1
    });

    // ১. ফ্রন্টএন্ডে অ্যামাউন্ট যোগ করার লজিক
    const currentTotalAmount = logs.reduce((sum, log) => sum + (Number(log.amount) || 0), 0);

    // 🔥 NEW: লোগো খুঁজে বের করার ফাংশন
    const getServiceLogo = (type) => {
        if (!type) return '/logo.png';
        const tLower = type.toLowerCase();
        
        if (tLower.includes('bkash')) return '/bkash.jpg';
        if (tLower.includes('nagad')) return '/nagad.png';
        if (tLower.includes('rocket')) return '/rocket.png';
        if (tLower.includes('whatsapp')) return '/whatsapp.png';
        
        return '/logo.png';
    };

    const fetchData = useCallback(() => {
        setLoading(true);
        const isMaster = localStorage.getItem('isMaster') === 'true';
        let allowedUsers = [];
        try {
            allowedUsers = JSON.parse(localStorage.getItem('allowedUsers') || '[]');
        } catch (e) { allowedUsers = []; }

        socketService.emit('get_pending_list', { ...filters, isMaster, allowedUsers });
    }, [filters]);

    useEffect(() => {
        socketService.connect();
        
        socketService.on('receive_pending_list', (data) => {
            setLogs(data.logs);
            setPagination({ 
                totalPages: data.totalPages, 
                currentPage: data.currentPage, 
                totalRows: data.totalRows 
            });
            setLoading(false);
        });

        socketService.on('new_pending_added', (newLog) => {
            setLogs((prevLogs) => [newLog, ...prevLogs.slice(0, 99)]);
        });

        socketService.on('pending_status_updated', (updatedData) => {
            setLogs((prevLogs) => 
                prevLogs.map(log => log.guid === updatedData.guid ? { ...log, status: updatedData.status } : log)
            );
        });

        fetchData();

        return () => {
            socketService.off('receive_pending_list');
            socketService.off('new_pending_added');
            socketService.off('pending_status_updated');
        };
    }, [fetchData]);

    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
    
    const handleReset = () => setFilters({ 
        username: '', 
        number: '', 
        security_code: '', 
        type: '', 
        status: '', 
        start_date: '', 
        end_date: '', 
        page: 1 
    });

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setFilters(prev => ({ ...prev, page: newPage }));
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const { currentPage, totalPages } = pagination;
        const maxVisibleButtons = 5; 
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button className="page-link shadow-none" onClick={() => handlePageChange(i)}>{i}</button>
                </li>
            );
        }
        return pages;
    };

    return (
        <Layout>
            <div className="main-content-area py-3 px-10">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="section-title m-0">⏳ Pending Transactions</h5>
                    {/* ২. এখানে Total Amount এবং Total Requests দেখানো হয়েছে */}
                    <div className="d-flex gap-2">
                        <div className="count-badge shadow-sm border-success-subtle">
                            Total Amount: <span className="text-success">৳{currentTotalAmount.toLocaleString()}</span>
                        </div>
                        <div className="count-badge shadow-sm">
                            Total Requests: <span>{pagination.totalRows}</span>
                        </div>
                    </div>
                </div>

                <div className="filter-card shadow-sm border-0 mb-4">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-2">
                            <label className="field-label">USERNAME</label>
                            <input type="text" name="username" className="modern-input" placeholder="User..." value={filters.username} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2">
                            <label className="field-label">TARGET NUMBER</label>
                            <input type="text" name="number" className="modern-input" placeholder="Number..." value={filters.number} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-1">
                            <label className="field-label">Security Codes</label>
                            <input type="text" name="security_code" className="modern-input" placeholder="Security code" value={filters.security_code} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-1">
                            <label className="field-label">METHOD</label>
                            <select name="type" className="modern-input" value={filters.type} onChange={handleFilterChange}>
                                <option value="">All</option>
                                <option value="Bkash">Bkash</option>
                                <option value="Nagad">Nagad</option>
                                <option value="Rocket">Rocket</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label className="field-label">STATUS</label>
                            <select name="status" className="modern-input" value={filters.status} onChange={handleFilterChange}>
                                <option value="">All</option>
                                <option value="pending">Pending</option>
                                <option value="executing">Executing</option>
                                <option value="success">Success</option>
                                <option value="failed">Failed</option>
                                <option value="cancel">Cancel</option>
                            </select>
                        </div>
                        <div className="col-md-1">
                            <label className="field-label">START</label>
                            <input type="date" name="start_date" className="modern-input" value={filters.start_date} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-1">
                            <label className="field-label">END</label>
                            <input type="date" name="end_date" className="modern-input" value={filters.end_date} onChange={handleFilterChange} />
                        </div>
                        <div className="col-md-2 d-flex gap-2">
                            <button className="btn btn-apply fw-bold w-100" onClick={fetchData}>Apply</button>
                            <button className="btn btn-reset fw-bold w-100" onClick={handleReset}>Reset</button>
                        </div>
                    </div>
                </div>

                <div className="table-container shadow-sm border-0 mb-3">
                    <div className="table-responsive">
                        <table className="custom-zebra-table">
                            <thead>
                                <tr>
                                    <th className="ps-4">USER & DEVICE</th>
                                    <th>TYPE & AMOUNT</th>
                                    <th>TARGET & SIM</th>
                                    <th style={{ minWidth: '350px' }}>TRANSACTION SMS</th>
                                    <th>DATE & TIME</th>
                                    <th className="text-center">STATUS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="6" className="text-center py-5">Loading...</td></tr>
                                ) : logs.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-5 opacity-50">No data found</td></tr>
                                ) : logs.map((log) => (
                                    <tr key={log._id}>
                                        <td className="ps-4">
                                            <div className="user-primary">{log.userId?.username || 'admin'}</div>
                                            <div className="device-secondary text-uppercase">{log.deviceId?.deviceName || 'mi'}</div>
                                        </td>
                                        
                                        {/* 🔥 MODIFIED: Logo Added Here */}
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <ResilientImage
                                                    src={getServiceLogo(log.type)} 
                                                    alt="logo" 
                                                    style={{
                                                        width: '32px', 
                                                        height: '32px', 
                                                        borderRadius: '50%', 
                                                        marginRight: '10px',
                                                        border: '1px solid #e2e8f0',
                                                        padding: '2px',
                                                        objectFit: 'contain',
                                                        background: '#fff'
                                                    }} 
                                                />
                                                <div>
                                                    <div className="sim-num text-uppercase">{log.type}</div>
                                                    <div className="sender-tag" style={{color: '#0284c7', fontSize: '14px'}}>৳{log.amount}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="date-bold text-primary">{log.number || 'N/A'}</div>
                                            <div className="time-muted">SIM: {log.sim_number || 'N/A'}</div>
                                            {log.checkapi && <span className="badge bg-info text-white mt-1" style={{fontSize: '9px'}}>API Verified</span>}
                                        </td>
                                        <td className="msg-body py-3">
                                            {log.sms || "Waiting for SMS..."}
                                            <div className="mt-1 d-flex gap-1">
                                                {log.security_code && (
                                                    <span className="badge bg-warning text-dark border" style={{fontSize: '10px'}}>
                                                        OTP: {log.security_code.length > 3 
                                                            ? '***' + log.security_code.substring(3) 
                                                            : '***'}
                                                    </span>
                                                )}
                                                {log.resent > 0 && <span className="badge bg-secondary text-white" style={{fontSize: '10px'}}>Resent: {log.resent}</span>}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="date-bold">{moment(log.createdAt).tz('Asia/Dhaka').format('DD MMM, YYYY')}</div>
                                            <div className="time-muted">{moment(log.createdAt).tz('Asia/Dhaka').format('hh:mm A')}</div>
                                        </td>
                                        
                                        {/* 🔥 STATUS COLUMN MODIFIED 🔥 */}
                                        <td className="text-center">
                                            <span className={`badge-status ${
                                                log.status === 'success' ? 'bg-seen' : 
                                                (log.status === 'failed' || log.status === 'cancel') ? 'bg-fake' : 
                                                log.status === 'executing' ? 'bg-timeout' : 'badge-unseen'
                                            }`}>
                                                {log.status}
                                            </span>
                                            
                                            {/* 🔥 লাল রঙের Regenerated ব্যাজ */}
                                            {log.isRegenerated && (
                                                <div className="mt-1">
                                                    <span className="badge bg-danger text-white border" style={{fontSize: '9px', fontWeight: 'bold'}}>
                                                        Regenerated
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center px-2 py-2">
                    <div className="text-muted small">
                        Showing Page <b>{pagination.currentPage}</b> of <b>{pagination.totalPages}</b>
                    </div>
                    <nav>
                        <ul className="pagination pagination-sm m-0 gap-1">
                            <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(pagination.currentPage - 1)}>Previous</button>
                            </li>
                            {renderPageNumbers()}
                            <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                                <button className="page-link shadow-none" onClick={() => handlePageChange(pagination.currentPage + 1)}>Next</button>
                            </li>
                        </ul>
                    </nav>
                </div>
            </div>

            <style>{`
                .main-content-area { background-color: #f4f7fa; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .section-title { color: #334155; font-weight: 700; }
                .count-badge { background: #fff; padding: 5px 15px; border-radius: 50px; border: 1px solid #e2e8f0; font-size: 13px; }
                .count-badge span { color: #0284c7; font-weight: 800; }
                .border-success-subtle { border-color: #bbf7d0 !important; }
                .filter-card { background: #ffffff; padding: 15px; border-radius: 12px; }
                .field-label { font-size: 10px; font-weight: 700; color: #64748b; margin-bottom: 5px; text-transform: uppercase; }
                .modern-input { background: #f8fafc; border: 1px solid #cbd5e1; padding: 7px 10px; border-radius: 8px; font-size: 12px; width: 100%; outline: none; }
                .btn-apply { background: #0284c7; color: white; border-radius: 8px; font-size: 12px; transition: 0.3s; border: none; }
                .btn-apply:hover { background: #0369a1; transform: translateY(-1px); }
                .btn-reset { background: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 12px; }
                .table-container { background: #ffffff; border-radius: 12px; overflow: hidden; }
                .custom-zebra-table { width: 100%; border-collapse: collapse; }
                .custom-zebra-table thead { background: #e2e8f0; color: #475569; }
                .custom-zebra-table th { padding: 14px 15px; font-size: 11px; font-weight: 800; text-transform: uppercase; border-bottom: 2px solid #cbd5e1; text-align: left; }
                .custom-zebra-table tbody tr:nth-child(even) { background-color: #f8fafc; }
                .custom-zebra-table td { padding: 12px 15px; vertical-align: middle; border-bottom: 1px solid #edf2f7; }
                .user-primary { color: #0369a1; font-weight: 700; font-size: 14px; }
                .device-secondary { font-size: 10px; color: #0000009d; font-weight: 800; background: #e2e8f0; display: inline-block; padding: 1px 6px; border-radius: 4px; margin-top: 3px; }
                .sim-num { color: #1e293b; font-weight: 600; font-size: 13px; }
                .sender-tag { color: #4744efa4; font-weight: 800; font-size: 11px; }
                .msg-body { color: #334155; font-size: 12.5px; line-height: 1.5; }
                .date-bold { color: #334155; font-weight: 700; font-size: 13px; }
                .time-muted { font-size: 11px; color: #64748b; }
                .page-link { color: #475569; border: 1px solid #e2e8f0; border-radius: 6px !important; padding: 5px 12px; font-weight: 600; background: white; }
                .page-item.active .page-link { background-color: #0284c7; border-color: #0284c7; color: white; }
                .badge-status { padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid transparent; text-transform: capitalize; }
                .bg-seen { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
                .bg-fake { background: #fee2e2; color: #dc2626; border-color: #fecaca; }
                .bg-timeout { background: #ede9fe; color: #7c3aed; border-color: #ddd6fe; }
                .badge-unseen { background: #f3f4f6; color: #6b7280; padding: 4px 12px; border-radius: 6px; font-size: 11px; font-weight: 700; border: 1px solid #e5e7eb; }
            `}</style>
        </Layout>
    );
};

export default PendingList;
